import type { TFunction } from 'i18next'
import { phraseKey } from './spell-phrases.js'

/**
 * Параметры заклинания по-русски: время накладывания, дистанция,
 * компоненты, длительность.
 *
 * С сервера они приходят строками источника — «Action», «60 feet»,
 * «V, S, M (burning incense worth 10+ GP…)», «Concentration, up to 1
 * minute», — и до этой правки так и стояли в карточке: русское
 * описание, английская шапка.
 *
 * Разбирать их приходится здесь, а не в контенте: в SRD это одна
 * строка на поле, и разложить её на число и единицу — работа показа,
 * а не правил. Расчёт по этим строкам не идёт ни здесь, ни на сервере:
 * дистанция и длительность в движке не участвуют (D5).
 *
 * Что не разбирается грамматикой — материальный компонент в скобках
 * и условие реакции, — переводится целыми оборотами
 * (`spell-phrases.ts`). Оборот без перевода показывается
 * по-английски, а не пустотой, и ловится тестом.
 *
 * Единицы: футы всегда словом — «60 футов», как в описаниях.
 * Единообразие с уже переведённым текстом важнее краткости.
 */

/** Единица времени и расстояния: множественное число сводится к одной. */
const UNITS: Readonly<Record<string, string>> = {
  round: 'round',
  rounds: 'round',
  minute: 'minute',
  minutes: 'minute',
  hour: 'hour',
  hours: 'hour',
  day: 'day',
  days: 'day',
  mile: 'mile',
  miles: 'mile',
}

/** Перевод свободного оборота; нет перевода — оригинал, а не пустота. */
function phrase(t: TFunction, text: string): string {
  return t(`spellPhrase.${phraseKey(text)}`, { defaultValue: text })
}

/**
 * «10 minutes» → «10 минут». `of` — форма после «до»: «до 10 минут»,
 * где то же число требует родительного падежа.
 */
function amountText(t: TFunction, text: string, of: boolean): string | null {
  const match = /^(\d+) ([A-Za-z]+)$/.exec(text)
  if (match === null) return null
  const [, count, word] = match
  const unit = UNITS[(word ?? '').toLowerCase()]
  if (count === undefined || unit === undefined) return null
  return t(`spells.${of ? 'unitOf' : 'unit'}.${unit}`, { count: Number(count) })
}

/** Время накладывания: «Action or Ritual» → «Действие или ритуал». */
export function castingTimeText(t: TFunction, value: string): string {
  // Целиком заданный перевод бьёт разбор: у «Роста растений» два
  // времени с пояснениями, и грамматики под это заводить не стоит.
  const whole = t(`spellPhrase.${phraseKey(value)}`, { defaultValue: '' })
  if (whole !== '') return whole

  const suffix = ' or Ritual'
  const ritual = value.endsWith(suffix)
  const head = ritual ? value.slice(0, -suffix.length) : value
  const text = castingHead(t, head)
  return ritual ? t('spells.fact.orRitual', { time: text }) : text
}

function castingHead(t: TFunction, head: string): string {
  if (head === 'Action') return t('spells.fact.action')
  if (head === 'Bonus Action') return t('spells.fact.bonusAction')
  if (head === 'Reaction') return t('spells.fact.reaction')

  // «Bonus Action, which you take immediately after…»: условие —
  // свободный текст, и переводится оборотом целиком.
  const tail = /^(Bonus Action|Reaction), (which you take .+)$/.exec(head)
  if (tail !== null) {
    return t('spells.fact.withTail', {
      head: t(
        tail[1] === 'Reaction' ? 'spells.fact.reaction' : 'spells.fact.bonusAction',
      ),
      tail: phrase(t, tail[2] ?? ''),
    })
  }

  return amountText(t, head, false) ?? head
}

/** Дистанция: «Self» → «На себя», «60 feet» → «60 футов». */
export function rangeText(t: TFunction, value: string): string {
  if (value === 'Self') return t('spells.fact.self')
  if (value === 'Touch') return t('spells.fact.touch')
  if (value === 'Sight') return t('spells.fact.sight')
  if (value === 'Unlimited') return t('spells.fact.unlimited')
  if (value === 'Special') return t('spells.fact.special')

  const feet = /^(\d+) (?:foot|feet)$/.exec(value)
  if (feet?.[1] !== undefined) {
    return t('spells.fact.feet', { count: Number(feet[1]) })
  }

  return amountText(t, value, false) ?? phrase(t, value)
}

/** Длительность: «Concentration, up to 1 minute» → «Сосредоточение, до 1 минуты». */
export function durationText(t: TFunction, value: string): string {
  if (value === 'Instantaneous') return t('spells.fact.instantaneous')
  if (value === 'Special') return t('spells.fact.special')
  if (value === 'Until dispelled') return t('spells.fact.dispelled')
  if (value === 'Until dispelled or triggered') {
    return t('spells.fact.dispelledOrTriggered')
  }

  // Запятая после «Concentration» стоит не везде: у одного заклинания
  // в источнике её нет, и это разбору мешать не должно.
  const held = /^Concentration,? up to (.+)$/.exec(value)
  if (held?.[1] !== undefined) {
    return t('spells.fact.concentration', {
      amount: amountText(t, held[1], true) ?? held[1],
    })
  }

  const upTo = /^Up to (.+)$/.exec(value)
  if (upTo?.[1] !== undefined) {
    return t('spells.fact.upTo', { amount: amountText(t, upTo[1], true) ?? upTo[1] })
  }

  return amountText(t, value, false) ?? phrase(t, value)
}

/** Компоненты: «V, S, M (a candle)» → «В, С, М (свеча)». */
export function componentsText(t: TFunction, value: string): string {
  const open = value.indexOf(' (')
  const head = open === -1 ? value : value.slice(0, open)
  const letters = head
    .split(', ')
    .map((letter) =>
      t(`spells.fact.component.${letter.toLowerCase()}`, { defaultValue: letter }),
    )
    .join(', ')

  if (open === -1 || !value.endsWith(')')) return letters

  const material = value.slice(open + 2, -1)
  return t('spells.fact.material', { letters, text: phrase(t, material) })
}
